import { KnowledgeDoc } from "../types";

type BuildContextOptions = {
  query?: string;
  maxChars?: number;
  chunkSize?: number;
  chunkOverlap?: number;
};

const STOP_WORDS = new Set([
  "the", "and", "that", "with", "from", "this", "they", "them", "their", "there",
  "have", "has", "had", "will", "would", "should", "could", "into", "over", "under",
  "about", "your", "yours", "you", "are", "was", "were", "for", "not", "but", "all",
  "any", "can", "may", "our", "out", "off", "its", "it's", "his", "her", "she", "him",
  "who", "whom", "what", "when", "where", "why", "how", "than", "then", "also", "too"
]);

const normalize = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const extractTerms = (text: string) => {
  const terms = new Set<string>();
  const normalized = normalize(text);
  for (const word of normalized.split(" ")) {
    if (word.length < 4) continue;
    if (STOP_WORDS.has(word)) continue;
    terms.add(word);
  }
  return terms;
};

const chunkCache = new Map<string, string[]>();

const chunkText = (text: string, size: number, overlap: number, cacheKey?: string) => {
  if (cacheKey && chunkCache.has(cacheKey)) {
    return chunkCache.get(cacheKey) || [text];
  }
  if (text.length <= size) return [text];
  const chunks: string[] = [];
  let start = 0;
  while (start < text.length) {
    const end = Math.min(start + size, text.length);
    chunks.push(text.slice(start, end));
    if (end >= text.length) break;
    start = Math.max(0, end - overlap);
  }
  if (cacheKey) chunkCache.set(cacheKey, chunks);
  return chunks;
};

const scoreChunk = (chunk: string, terms: Set<string>) => {
  if (!terms.size) return 0;
  const normalized = normalize(chunk);
  let score = 0;
  for (const term of terms) {
    if (normalized.includes(term)) score += 1;
  }
  return score;
};

export const buildKnowledgeContext = (
  docs: KnowledgeDoc[],
  options: BuildContextOptions = {}
) => {
  const maxChars = options.maxChars ?? 12000;
  const chunkSize = options.chunkSize ?? 1400;
  const chunkOverlap = options.chunkOverlap ?? 200;
  const queryTerms = extractTerms(options.query || "");

  if (!docs?.length) return "";

  const items: { title: string; chunk: string; score: number }[] = [];

  for (const doc of docs) {
    if (!doc?.content) continue;
    const content = doc.content.trim();
    if (!content) continue;
    const title = doc.title || "Knowledge";

    // Always keep short system notes intact.
    if (doc.isSystem && content.length <= 1200) {
      items.push({ title, chunk: content, score: Number.MAX_SAFE_INTEGER });
      continue;
    }

    const cacheKey = `${doc.id || title}:${content.length}:${chunkSize}:${chunkOverlap}`;
    const chunks = chunkText(content, chunkSize, chunkOverlap, cacheKey);
    for (const chunk of chunks) {
      const score = scoreChunk(chunk, queryTerms);
      items.push({ title, chunk, score });
    }
  }

  items.sort((a, b) => b.score - a.score);

  const selected: string[] = [];
  let used = 0;
  for (const item of items) {
    if (used >= maxChars) break;
    const header = `SOURCE: ${item.title}\n`;
    const body = item.chunk.trim();
    if (!body) continue;
    const block = `${header}${body}\n`;
    if (used + block.length > maxChars && used > 0) break;
    selected.push(block);
    used += block.length;
  }

  return selected.join("\n");
};
